import { ChangeEvent, useEffect, useState, useCallback } from "react"
import { Box, TextField } from "@material-ui/core"
import { queryCache, useMutation, useQuery } from "react-query"
import { updateMarket } from "api/regions"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { css } from "@emotion/css"
import { styles } from "./styles"
import ConfirmationModal from "components/Modals/ConfirmationModal"
import { fetchCurrentMarket } from "api/markets"

const maxTolerance = 60

export const MaxSpotMatchingTolerance = () => {
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const { layerId } = meta ?? {}
  const [value, setValue] = useState<number>()
  const [disabled, setDisabled] = useState<boolean>(false)
  const [inputHasError, setInputHasError] = useState<boolean>(false)
  const [inputErrorMessage, setInputErrorMessage] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [prevValue, setPrevValue] = useState<number>()
  const { data: market } = useQuery("current-market", fetchCurrentMarket)

  /**
   * Validate input value and set error message
   */
  const validateInputValue = useCallback((inputVal: number | undefined) => {
    if (
      inputVal !== undefined &&
      (isNaN(inputVal) || inputVal > maxTolerance || inputVal < 0)
    ) {
      setInputErrorMessage(
        "market.max.spot.matching.tolerance.input.error.message",
      )
      setInputHasError(true)
    } else {
      setInputHasError(false)
      setInputErrorMessage("")
    }
  }, [])

  useEffect(() => {
    validateInputValue(market?.maxMatchingTimeTolerance ?? maxTolerance)
    setValue(market?.maxMatchingTimeTolerance ?? maxTolerance)
    setPrevValue(market?.maxMatchingTimeTolerance ?? maxTolerance)
  }, [market, validateInputValue])

  const [patch] = useMutation(updateMarket, {
    onSuccess: () => {
      queryCache.invalidateQueries("current-market")
      setPrevValue(value)
      resetComponent()
    },
    onError: () => {
      setDisabled(false)
      setValue(prevValue)
      setInputHasError(true)
      setInputErrorMessage(
        "market.max.spot.matching.tolerance.input.error.service",
      )
    },
  })

  /**
   * Reset component to initial state
   */
  const resetComponent = () => {
    setInputHasError(false)
    setInputErrorMessage("")
    setIsModalOpen(false)
    setDisabled(false)
  }

  /**
   * On change input value
   */
  const onChangeMaxSpotTolerance = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event?.target?.value ?? ""
    const regex = /\D/gi
    const sanitizedValue = parseInt(inputValue.replaceAll(regex, ""))
    validateInputValue(sanitizedValue)
    setValue(sanitizedValue)
  }

  /**
   * On blur input value
   */
  const onBlurMaxSpotTolerance = () => {
    if (!inputHasError && prevValue !== value) {
      setIsModalOpen(true)
    }
  }

  /**
   * Fired when user press confirm button
   */
  const onSubmit = () => {
    setIsModalOpen(false)
    setDisabled(true)
    patch({
      id: layerId,
      maxMatchingTimeTolerance: value,
    })
  }

  return (
    <Box className={styles.container}>
      <div className="BodyBody1Regular">
        {`${t("market.max.spot.matching.tolerance")}*:`}
      </div>
      <TextField
        className={css`
          grid-row: 1;
          grid-column: 2;
          margin-left: 8px;
        `}
        id="maxSpotMatchingTolerance"
        variant="outlined"
        size="small"
        type="number"
        InputProps={{
          endAdornment: (
            <div style={{ color: "#808080" }}>
              {t("market.max.spot.matching.tolerance.info.message.time.unit")}
            </div>
          ),
          inputProps: { min: 1, max: maxTolerance },
        }}
        value={value}
        onChange={onChangeMaxSpotTolerance}
        onBlur={onBlurMaxSpotTolerance}
        disabled={disabled}
        error={inputHasError}
        helperText={t(inputErrorMessage)}
      />

      <div
        className={css`
          padding: 8px 10px;
          border-radius: 4px;
          margin-top: 8px;
          color: black;
          display: grid;
          grid-column: span 2;
          grid-row: 2;
        `}
      >
        <p className="BodyBody2Regular">
          *{t("market.max.spot.matching.tolerance.info.message")}{" "}
          <strong>
            {maxTolerance}{" "}
            {t("market.max.spot.matching.tolerance.info.message.time.unit")}
          </strong>
        </p>
      </div>

      <ConfirmationModal
        open={isModalOpen}
        type="primary_destructive"
        onCancel={() => {
          setIsModalOpen(false)
          setValue(prevValue)
        }}
        onSubmit={onSubmit}
        title={t("market.max.spot.matching.tolerance.modal.title")}
        description={`${t(
          "market.max.spot.matching.tolerance.modal.message",
        )} ${value} ${t(
          "market.max.spot.matching.tolerance.info.message.time.unit",
        )}?`}
        submitLabel={t("market.max.spot.matching.tolerance.modal.button")}
      />
    </Box>
  )
}
